import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, Select, MenuItem, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, LinearProgress, IconButton, Tooltip, Dialog, Backdrop, DialogTitle,
  DialogContent, DialogActions, List, ListItem, ListItemText,
  TableSortLabel, TextField, Checkbox, Pagination, Snackbar, Alert
} from '@mui/material';
import { AutoFixHigh, CheckCircle, Warning, SwapHoriz, DeleteSweep, DoneAll, EventNote, Delete } from '@mui/icons-material';
import axios from 'axios';

type Order = 'asc' | 'desc';

const Distribution: React.FC = () => {
  const [periods, setPeriods] = useState<any[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<number>(0);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [distributing, setDistributing] = useState(false);
  
  const [filterDate, setFilterDate] = useState<string>('');
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<string>('date');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  
  const [page, setPage] = useState(1);
  const itemsPerPage = 15;

  const [openSwap, setOpenSwap] = useState(false);
  const [currentSlot, setCurrentSlot] = useState<any>(null);
  const [candidates, setCandidates] = useState<any[]>([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    axios.get('/api/periods').then(res => {
      setPeriods(res.data);
      const active = res.data.find((p: any) => p.is_active === 1);
      if (active) setSelectedPeriod(active.id);
      else if (res.data.length > 0) setSelectedPeriod(res.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (selectedPeriod) fetchAssignments(selectedPeriod);
  }, [selectedPeriod]);

  useEffect(() => {
    setPage(1);
  }, [filterDate, selectedPeriod]);

  const fetchAssignments = async (periodId: number) => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/assignments/period/${periodId}`);
      setAssignments(res.data);
      setSelectedIds([]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAutoDistribute = async () => {
    if (!window.confirm('Запустить автоматическое распределение? Текущие предложения будут перезаписаны.')) return;
    setDistributing(true);
    try {
      await axios.post('/api/assignments/auto-distribute', { periodId: selectedPeriod });
      await fetchAssignments(selectedPeriod);
      setSnackbar({ open: true, message: 'Экскурсоводы успешно распределены!', severity: 'success' });
    } catch (err: any) {
      console.error(err);
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout') || err.message?.includes('Network Error')) {
        await fetchAssignments(selectedPeriod);
        setSnackbar({ open: true, message: 'Распределение завершено (соединение было прервано, но данные сохранены)', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'Ошибка при распределении экскурсоводов', severity: 'error' });
      }
    } finally {
      setDistributing(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm('Удалить все предложенные варианты? Утвержденные останутся.')) return;
    try {
      await axios.delete(`/api/assignments/clear/${selectedPeriod}`);
      await fetchAssignments(selectedPeriod);
      setSnackbar({ open: true, message: 'Предложения успешно очищены', severity: 'success' });
    } catch (err: any) {
      console.error('Ошибка очистки:', err);
      setSnackbar({ open: true, message: 'Ошибка очистки данных', severity: 'error' });
    }
  };

  const handleApprove = async (id: number) => {
    try {
      await axios.put(`/api/assignments/${id}`, { status: 'approved' });
      fetchAssignments(selectedPeriod);
    } catch (err) {
      setSnackbar({ open: true, message: 'Ошибка утверждения', severity: 'error' });
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.length === 0) return;
    try {
      await axios.post('/api/assignments/approve-bulk', { ids: selectedIds });
      fetchAssignments(selectedPeriod);
    } catch (err) {
      setSnackbar({ open: true, message: 'Ошибка массового утверждения', severity: 'error' });
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Удалить это назначение?')) return;
    try {
      await axios.delete(`/api/assignments/${id}`);
      fetchAssignments(selectedPeriod);
    } catch (err) {
      setSnackbar({ open: true, message: 'Ошибка удаления', severity: 'error' });
    }
  };

  const handleOpenSwap = async (slot: any) => {
    setCurrentSlot(slot);
    setOpenSwap(true);
    try {
      const res = await axios.get(`/api/assignments/${slot.id}/candidates`);
      setCandidates(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSwap = async (guideId: number) => {
    try {
      await axios.put(`/api/assignments/${currentSlot.id}`, { guideId, status: 'approved' });
      setOpenSwap(false);
      fetchAssignments(selectedPeriod);
    } catch (err) {
      setSnackbar({ open: true, message: 'Ошибка замены', severity: 'error' });
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const cleanDate = dateStr.split('T')[0];
    const [year, month, day] = cleanDate.split('-');
    const d = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
    const days = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];
    return `${days[d.getUTCDay()]}, ${String(d.getUTCDate()).padStart(2, '0')}.${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  };

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filteredAssignments = assignments.filter(item => {
    if (!filterDate) return true;
    return item.date.split('T')[0] === filterDate;
  });

  const sortedAssignments = [...filteredAssignments].sort((a, b) => {
    let valA = a[orderBy] || '';
    let valB = b[orderBy] || '';
    if (orderBy === 'date') {
      const dateCompare = a.date.localeCompare(b.date);
      if (dateCompare !== 0) return order === 'asc' ? dateCompare : -dateCompare;
      return order === 'asc' ? (a.time || '').localeCompare(b.time || '') : (b.time || '').localeCompare(a.time || '');
    }
    if (order === 'asc') return valA < valB ? -1 : 1;
    return valA > valB ? -1 : 1;
  });

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = sortedAssignments.filter(a => a.status === 'proposed').map(a => a.id);
      setSelectedIds(allIds);
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: number) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(item => item !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const isAllSelected = sortedAssignments.length > 0 && sortedAssignments.filter(a => a.status === 'proposed').every(a => selectedIds.includes(a.id));

  const paginatedAssignments = sortedAssignments.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const totalPages = Math.ceil(sortedAssignments.length / itemsPerPage);

  return (
    <Box sx={{ p: 3 }}>


      <Paper sx={{ p: 2, mb: 3, borderRadius: 2, position: 'relative', overflow: 'hidden' }}>
        {distributing && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3 }} />}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Период</InputLabel>
            <Select value={selectedPeriod} label="Период" onChange={(e) => setSelectedPeriod(Number(e.target.value))}>
              {periods.map(p => <MenuItem key={p.id} value={p.id}>{p.month_name} {p.year}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField type="date" size="small" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} sx={{ width: 180 }} variant="outlined" />
          <Box sx={{ flexGrow: 1 }} />
          {selectedIds.length > 0 && (
            <Button variant="contained" color="success" startIcon={<DoneAll />} onClick={handleBulkApprove}>
              Утвердить выбранные ({selectedIds.length})
            </Button>
          )}
          <Button variant="outlined" color="error" startIcon={<DeleteSweep />} disabled={distributing} onClick={handleClear}>
            Очистить предложения
          </Button>
          <Button 
            variant="contained" 
            color="secondary" 
            startIcon={distributing ? <CircularProgress size={20} color="inherit" /> : <AutoFixHigh />} 
            onClick={handleAutoDistribute} 
            disabled={distributing || !selectedPeriod}
            sx={{ minWidth: 180 }}
          >
            {distributing ? 'Распределение...' : 'Распределить'}
          </Button>
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: 'grey.50' }}>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox indeterminate={selectedIds.length > 0 && !isAllSelected} checked={isAllSelected} onChange={handleSelectAll} disabled={sortedAssignments.filter(a => a.status === 'proposed').length === 0} />
                  </TableCell>
                  <TableCell><TableSortLabel active={orderBy === 'date'} direction={orderBy === 'date' ? order : 'asc'} onClick={() => handleRequestSort('date')}>Дата / Время</TableSortLabel></TableCell>
                  <TableCell><TableSortLabel active={orderBy === 'tour_name'} direction={orderBy === 'tour_name' ? order : 'asc'} onClick={() => handleRequestSort('tour_name')}>Экскурсия</TableSortLabel></TableCell>
                  <TableCell>Программа экскурсии</TableCell>
                  <TableCell align="center">Кол-во</TableCell>
                  <TableCell>Заказчик</TableCell>
                  <TableCell>Класс</TableCell>
                  <TableCell>Язык</TableCell>
                  <TableCell><TableSortLabel active={orderBy === 'guide_name'} direction={orderBy === 'guide_name' ? order : 'asc'} onClick={() => handleRequestSort('guide_name')}>Назначенный гид</TableSortLabel></TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell align="right">Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedAssignments.length === 0 ? (
                  <TableRow><TableCell colSpan={11} align="center">Нет данных. {filterDate ? 'Попробуйте сбросить фильтр даты.' : 'Нажмите "Распределить".'}</TableCell></TableRow>
                ) : (
                  paginatedAssignments.map((row) => (
                    <TableRow key={row.id} hover selected={selectedIds.includes(row.id)}>
                      <TableCell padding="checkbox">
                        <Checkbox checked={selectedIds.includes(row.id)} onChange={() => handleSelectOne(row.id)} disabled={row.status !== 'proposed'} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2" fontWeight={500}>{formatDate(row.date)}</Typography>
                          <Typography variant="caption" color="textSecondary">{row.time?.slice(0, 5) || '--:--'}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 250 }}>{row.tour_name}</TableCell>
                      <TableCell sx={{ maxWidth: 200, fontSize: '0.8rem', color: 'text.secondary' }}>
                        {row.program_name || '—'}
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={row.people_count || 0} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 150 }} title={row.customer || ''}>
                          {row.customer || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.class_name || '—'}</TableCell>
                      <TableCell>{row.is_english ? '🇬🇧 Англ' : '🇷🇺 Рус'}</TableCell>
                      <TableCell>
                        {row.guide_name ? (
                          <Chip label={row.guide_short || row.guide_name} size="small" color="primary" variant="outlined" />
                        ) : (
                          <Chip label="Нет кандидатов" size="small" color="error" icon={<Warning />} />
                        )}
                      </TableCell>
                      <TableCell>
                        {row.status === 'approved' ? (
                          <Chip label="Утвержден" size="small" color="success" icon={<CheckCircle />} />
                        ) : (
                          <Chip label="Предложен" size="small" color="warning" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {row.status === 'proposed' && row.guide_id && (
                          <Tooltip title="Утвердить"><IconButton color="success" onClick={() => handleApprove(row.id)}><CheckCircle /></IconButton></Tooltip>
                        )}
                        <Tooltip title="Заменить гида"><IconButton color="primary" onClick={() => handleOpenSwap(row)}><SwapHoriz /></IconButton></Tooltip>
                        <Tooltip title="Удалить назначение"><IconButton color="error" onClick={() => handleDelete(row.id)}><Delete /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 1 }}>
              <Pagination count={totalPages} page={page} onChange={(event, value) => setPage(value)} color="primary" size="large" showFirstButton showLastButton />
            </Box>
          )}
        </>
      )}

      <Dialog open={openSwap} onClose={() => setOpenSwap(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Заменить гида</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Экскурсия: {currentSlot?.tour_name} ({formatDate(currentSlot?.date)} {currentSlot?.time})
          </Typography>
          <List>
            {candidates.length === 0 ? (
              <Typography color="error">Нет доступных гидов для этой экскурсии</Typography>
            ) : (
              candidates.map(c => (
                <ListItem button key={c.id} onClick={() => handleSwap(c.id)} sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover', borderRadius: 1 } }}>
                  <ListItemText primary={c.name} secondary={c.short_name} />
                </ListItem>
              ))
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSwap(false)}>Отмена</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Distribution;
